/* eslint-disable */
import * as turf from "@turf/turf";
import _ from "lodash";
import mapboxgl from "mapbox-gl";
import aLineIcon from "../../assets/images/a-line-img.svg";
import bLineIcon from "../../assets/images/b-line-img.svg";
import type {
  AbLine,
  NewTrialStateInterface,
} from "../../pages/new-trail/types";
import type { PolygonGeoJSON } from "../components/MapComponent/type";
import {
  checkAlignmentWithCurrentState,
  clearStripLinesCache,
} from "../components/MapComponent/utils";
import { drawMachineryGridPure } from "./abLine";

const FEATURES_INDEX_ZERO = 0;
const INDEX_ZERO = 0;
const INDEX_ONE = 1;
const WHITE_COLOR = "white";
let previousAbLine: AbLine[] | null = null;
const mapboxMarkers: Record<string, mapboxgl.Marker> = {};

export const addABLinesToMap = (
  map: mapboxgl.Map | null,
  newTrialState: NewTrialStateInterface,
  setNewTrialState: any,
  polygonGeoJSON: PolygonGeoJSON,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  getAbLineColor: (id: number, type: string) => string,
  currentStep?: number,
  trialProtocolData?: any
): void => {
  if (!map || !newTrialState.ab_line) return;

  const polygon = polygonGeoJSON.features[FEATURES_INDEX_ZERO].geometry;

  if (previousAbLine) {
    const previousIds = new Set(
      previousAbLine.map((abItem: AbLine) => abItem.id)
    );
    const currentIds = new Set(
      newTrialState.ab_line.map((abItem: AbLine) => abItem.id)
    );

    previousIds.forEach((id) => {
      if (!currentIds.has(id)) {
        removeLayerAndSources(map, id as number);
      }
    });
  }

  newTrialState.ab_line.forEach((abItem: AbLine, abLineIndex: number) => {
    const sourceId = `ab-line-geojson-data-${abItem.id as number}`;
    const layerId = `ab-line-layer-${abItem.id as number}`;
    if (!map.getSource(sourceId) || !map.getLayer(layerId)) {
      createNewLineLayer(
        map,
        abItem,
        polygon,
        createCustomMarkerElement,
        sourceId,
        layerId,
        newTrialState,
        setNewTrialState,
        polygonGeoJSON,
        getAbLineColor,
        abLineIndex,
        currentStep,
        trialProtocolData
      );
    } else {
      // Markers are always recreated in addMarkersToAbLine, so just call it
      const [marker1, marker2] = addMarkersToAbLine(
        map,
        abItem.geojson.geometry.coordinates[INDEX_ZERO],
        abItem.geojson.geometry.coordinates[INDEX_ONE],
        abItem,
        createCustomMarkerElement,
        sourceId,
        polygonGeoJSON,
        getAbLineColor,
        setNewTrialState,
        abLineIndex,
        currentStep,
        trialProtocolData
      );

      updateLayerVisibilityAndSelection(
        map,
        layerId,
        abItem.visible,
        abItem.id as number,
        currentStep === 1,
        polygonGeoJSON
      );

      const machinaryGridArray = getMachineryGridArray(newTrialState);

      updateLineSource(
        machinaryGridArray,
        marker1,
        marker2,
        map,
        abItem,
        polygonGeoJSON,
        getAbLineColor,
        trialProtocolData,
        currentStep
      );
      // Only update the line source and state on drag, not on every render
      // updateLineSourceOnDrag is now only called from the drag handler in addMarkersToAbLine
    }
  });
  previousAbLine = newTrialState.ab_line.map((abItem) => ({ ...abItem }));
};

const addMarkersToAbLine = (
  map: mapboxgl.Map,
  pointA: mapboxgl.LngLatLike,
  pointB: mapboxgl.LngLatLike,
  abItem: AbLine,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  sourceId: string,
  polygonGeoJSON: any,
  getAbLineColor: (id: number, type: string) => string,
  setNewTrialState?: (
    updater: (prevState: NewTrialStateInterface) => NewTrialStateInterface
  ) => void,
  abLineIndex?: number,
  currentStep?: number,
  trialProtocolData?: any
): [mapboxgl.Marker, mapboxgl.Marker] => {
  const marker1Id = `marker1-${abItem.id}`;
  const marker2Id = `marker2-${abItem.id}`;

  // Remove existing markers if they exist
  if (mapboxMarkers[marker1Id]) {
    mapboxMarkers[marker1Id].remove();
    delete mapboxMarkers[marker1Id];
  }
  if (mapboxMarkers[marker2Id]) {
    mapboxMarkers[marker2Id].remove();
    delete mapboxMarkers[marker2Id];
  }

  // Create new markers
  const marker1 = createMarker(
    map,
    pointA,
    aLineIcon,
    INDEX_ZERO,
    abItem.id as number,
    createCustomMarkerElement,
    abItem.selected
  );

  const marker2 = createMarker(
    map,
    pointB,
    bLineIcon,
    INDEX_ONE,
    abItem.id as number,
    createCustomMarkerElement,
    abItem.selected
  );

  const debouncedUpdateLineSourceOnDrag = _.debounce(() => {
    updateLineSourceOnDrag(
      marker1,
      marker2,
      map,
      sourceId,
      abItem,
      polygonGeoJSON,
      getAbLineColor,
      setNewTrialState,
      abLineIndex,
      trialProtocolData,
      currentStep
    );
  }, 500);

  marker1.on("drag", debouncedUpdateLineSourceOnDrag);
  marker2.on("drag", debouncedUpdateLineSourceOnDrag);

  return [marker1, marker2];
};

const createNewLineLayer = (
  map: mapboxgl.Map,
  abItem: AbLine,
  polygon: turf.Polygon | turf.MultiPolygon,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  sourceId: string,
  layerId: string,
  newTrialState: any,
  setNewTrialState: any,
  polygonGeoJSON: any,
  getAbLineColor: (id: number, type: string) => string,
  abLineIndex: number,
  currentStep?: number,
  trialProtocolData?: any
): void => {
  const [pointA, pointB] = getLinePoints(abItem, polygon);

  // Persist the initialized line coordinates so popup fields reflect
  // the exact AB line that is visible on the map.
  if (setNewTrialState) {
    setNewTrialState((prevState: NewTrialStateInterface) => {
      const currentLine = prevState.ab_line?.[abLineIndex];
      if (!currentLine) {
        return prevState;
      }

      const currentCoords = currentLine?.geojson?.geometry?.coordinates;
      const nextCoords = [pointA, pointB];
      if (JSON.stringify(currentCoords) === JSON.stringify(nextCoords)) {
        return prevState;
      }

      return {
        ...prevState,
        ab_line: prevState.ab_line.map((line, idx) =>
          idx === abLineIndex
            ? {
                ...line,
                geojson: {
                  ...line.geojson,
                  geometry: {
                    ...line.geojson.geometry,
                    type: "LineString",
                    coordinates: nextCoords,
                  },
                },
              }
            : line
        ),
      };
    });
  }

  if (abItem.id) {
    removeLayerAndSources(map, abItem.id);
  }

  const [marker1, marker2] = addMarkersToAbLine(
    map,
    pointA,
    pointB,
    abItem,
    createCustomMarkerElement,
    sourceId,
    polygonGeoJSON,
    getAbLineColor,
    setNewTrialState,
    abLineIndex,
    currentStep,
    trialProtocolData
  );

  const machinaryGridArray = getMachineryGridArray(newTrialState);

  const line = turf.lineString([pointA, pointB] as any);
  map.addSource(sourceId, { type: "geojson", data: line });
  map.addLayer({
    id: layerId,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": getAbLineColor(abItem?.id as number, "hexCode"),
      "line-width": 4,
      "line-dasharray": [2, 2],
    },
  });

  updateLineSource(
    machinaryGridArray,
    marker1,
    marker2,
    map,
    abItem,
    polygonGeoJSON,
    getAbLineColor,
    trialProtocolData,
    currentStep
  );
};

const getLinePoints = (
  abItem: AbLine,
  polygon: turf.Polygon | turf.MultiPolygon
): [mapboxgl.LngLatLike, mapboxgl.LngLatLike] => {
  if (
    abItem.geojson.geometry.coordinates[INDEX_ZERO] === 0 &&
    abItem.geojson.geometry.coordinates[INDEX_ONE] === 0
  ) {
    // Generate random points but very close to field boundary (5% margin)
    // This allows AB lines to extend outside but stay near the field edge
    const [minX, minY, maxX, maxY] = turf.bbox(polygon);
    const width = maxX - minX;
    const height = maxY - minY;

    // Use small 5% margin inward to keep points close to boundary
    const marginX = width * 0.05;
    const marginY = height * 0.05;
    const constrainedBbox: [number, number, number, number] = [
      minX + marginX,
      minY + marginY,
      maxX - marginX,
      maxY - marginY,
    ];

    const randomPoints = turf.randomPoint(2, { bbox: constrainedBbox });
    return [
      randomPoints.features[INDEX_ZERO].geometry
        .coordinates as mapboxgl.LngLatLike,
      randomPoints.features[INDEX_ONE].geometry
        .coordinates as mapboxgl.LngLatLike,
    ];
  } else {
    return [
      abItem.geojson.geometry.coordinates[INDEX_ZERO],
      abItem.geojson.geometry.coordinates[INDEX_ONE],
    ];
  }
};

const getMachineryGridArray = (newTrialState: any): any[] => {
  if (newTrialState.ab_line.length === 0) return [];
  return newTrialState.ab_line.map((abItem: any) => ({
    id: `grid-layer-${abItem.id as string}`,
    ABLine_id: abItem.id,
    name: "Machinery Grid",
    key: "Machinery Grid",
    grid_width: abItem.grid_width,
    visible: abItem.visible,
    rotation: 0,
  }));
};

const updateLineSourceOnDrag = (
  marker1: mapboxgl.Marker,
  marker2: mapboxgl.Marker,
  map: mapboxgl.Map,
  sourceId: string,
  abItem: AbLine,
  polygonGeoJSON: PolygonGeoJSON,
  getAbLineColor: (id: number, type: string) => string,
  setNewTrialState?: (
    updater: (prevState: NewTrialStateInterface) => NewTrialStateInterface
  ) => void,
  abLineIndex?: number,
  trialProtocolData?: any,
  currentStep?: number
): void => {
  const updatedPointA = marker1.getLngLat().toArray();
  const updatedPointB = marker2.getLngLat().toArray();
  const line = turf.lineString([updatedPointA, updatedPointB]);
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
  source.setData(line);
  abItem.geojson.geometry.coordinates = [updatedPointA, updatedPointB] as any;

  // Update ab_line coordinates in state so input reflects drag
  if (setNewTrialState && typeof abLineIndex === "number") {
    setNewTrialState((prevState: NewTrialStateInterface) => ({
      ...prevState,
      ab_line: prevState?.ab_line.map((line, idx) =>
        idx === abLineIndex
          ? {
              ...line,
              geojson: {
                ...line.geojson,
                geometry: {
                  ...line.geojson.geometry,
                  coordinates: [updatedPointA, updatedPointB],
                },
              },
            }
          : line
      ),
    }));

    // Clear strip lines cache to force immediate redraw with new AB-line direction
    clearStripLinesCache();
    // Check whether plots are still aligned with the new AB-line direction
    checkAlignmentWithCurrentState();
  }

  const sourceIdGrid = `grid-layer-${abItem.id ?? ""}`;
  //updateABWidth(abItem, sourceIdGrid, map, polygonGeoJSON);
  // Use turf.feature (not turf.polygon) so MultiPolygon field geometries are
  // passed through intact — the machinery grid is then clipped inside every
  // sub-polygon. turf.polygon() would throw on a MultiPolygon's coordinates.
  const turfPolygon = turf.feature(
    polygonGeoJSON.features[0].geometry
  ) as turf.Feature<turf.Polygon | turf.MultiPolygon>;

  const layerId = `grid-line-layer-${abItem.id ?? ""}`;
  const shouldDrawMiddleLines = true;
  drawMachineryGridPure(
    map,
    turfPolygon,
    updatedPointA,
    updatedPointB,
    sourceIdGrid,
    layerId,
    {
      spacing: abItem.grid_width,
      color: getAbLineColor(abItem.id as number, "hexCode"),
      drawGreyLines: shouldDrawMiddleLines,
    }
  );
};

const updateLineSource = (
  machinaryGridArray: any[],
  marker1: mapboxgl.Marker,
  marker2: mapboxgl.Marker,
  map: mapboxgl.Map,
  abItem: AbLine,
  polygonGeoJSON: PolygonGeoJSON,
  getAbLineColor: (id: number, type: string) => string,
  trialProtocolData?: any,
  currentStep?: number
): void => {
  const updatedPointA = marker1.getLngLat().toArray();
  const updatedPointB = marker2.getLngLat().toArray();
  const line = turf.lineString([updatedPointA, updatedPointB]);
  const source = map.getSource(
    `ab-line-geojson-data-${abItem.id}`
  ) as mapboxgl.GeoJSONSource;
  source.setData(line);
  abItem.geojson.geometry.coordinates = [updatedPointA, updatedPointB] as any;
  const angleDegrees = calculateAngleDegrees(updatedPointA, updatedPointB);

  const updatedMachineryGrid = machinaryGridArray.map((item: any) => {
    if (item.ABLine_id === abItem.id) {
      return { ...item, rotation: -angleDegrees };
    }
    return item;
  });
  // Use turf.feature (not turf.polygon) so MultiPolygon field geometries are
  // passed through intact — the machinery grid is then clipped inside every
  // sub-polygon. turf.polygon() would throw on a MultiPolygon's coordinates.
  const turfPolygon = turf.feature(
    polygonGeoJSON.features[0].geometry
  ) as turf.Feature<turf.Polygon | turf.MultiPolygon>;
  const sourceId = `grid-layer-${abItem.id ?? ""}`;
  const layerId = `grid-line-layer-${abItem.id ?? ""}`;
  const shouldDrawMiddleLines = true;
  drawMachineryGridPure(
    map,
    turfPolygon,
    updatedPointA,
    updatedPointB,
    sourceId,
    layerId,
    {
      spacing: abItem.grid_width,
      color: getAbLineColor(abItem.id as number, "hexCode"),
      drawGreyLines: shouldDrawMiddleLines,
    }
  );

  //drawLinesAndGrid(map,polygonGeoJSON.features[0],{spacing : newTrialState.machinery_grid?.[0]?.width},newTrialState.ab_line??[])
  //   addMachineryGridToMap(
  //     map,
  //     updatedMachineryGrid,
  //     polygonGeoJSON,
  //     getAbLineColor
  //   );
};

const calculateAngleDegrees = (pointA: number[], pointB: number[]): number => {
  const angleRadians = Math.atan2(pointB[1] - pointA[1], pointB[0] - pointA[0]);
  return (angleRadians * 180) / Math.PI;
};

const addMachineryGridToMap = (
  map: mapboxgl.Map | null,
  machinaryGridArray: any[],
  polygonGeoJSON: PolygonGeoJSON,
  getAbLineColor?: (id: number, type: string) => string
): void => {
  if (!map) return;

  machinaryGridArray.forEach((gridItem: any) => {
    const { rotation, grid_width, visible, ABLine_id } = gridItem;
    if (!grid_width) return;

    const bbox = turf.bbox(
      polygonGeoJSON.features[FEATURES_INDEX_ZERO].geometry
    );
    const expandedBbox = expandBbox(bbox);
    const grid = turf.squareGrid(
      expandedBbox as turf.helpers.BBox,
      grid_width,
      { units: "meters" }
    );
    const rotatedGrid = turf.transformRotate(grid, rotation);
    const gridWithinPolygon = getGridWithinPolygon(rotatedGrid, polygonGeoJSON);

    const sourceId = `grid-layer-${ABLine_id ?? ""}`;
    const layerId = `grid-line-layer-${ABLine_id ?? ""}`;

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: gridWithinPolygon,
        },
      });

      const gridColor = getAbLineColor
        ? getAbLineColor(ABLine_id as number, "hexCode")
        : WHITE_COLOR;
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": gridColor || WHITE_COLOR,
          "line-width": 2,
        },
      });
    } else {
      (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData({
        type: "FeatureCollection",
        features: gridWithinPolygon,
      });
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(
          layerId,
          "visibility",
          visible ? "visible" : "none"
        );
      }
    }
  });
};

const expandBbox = (bbox: turf.helpers.BBox): turf.helpers.BBox => {
  const bboxDiagonal = Math.sqrt(
    Math.pow(bbox[2] - bbox[0], 2) + Math.pow(bbox[3] - bbox[1], 2)
  );
  const centerX = (bbox[0] + bbox[2]) / 2;
  const centerY = (bbox[1] + bbox[3]) / 2;
  return [
    centerX - bboxDiagonal / 2,
    centerY - bboxDiagonal / 2,
    centerX + bboxDiagonal / 2,
    centerY + bboxDiagonal / 2,
  ];
};

const getGridWithinPolygon = (
  rotatedGrid: turf.FeatureCollection<turf.Polygon>,
  polygonGeoJSON: PolygonGeoJSON
): Array<turf.Feature<turf.Polygon>> => {
  return rotatedGrid.features
    .map(
      (cell) =>
        turf.intersect(
          cell.geometry,
          polygonGeoJSON.features[FEATURES_INDEX_ZERO].geometry
        ) as turf.Feature<turf.Polygon> | null
    )
    .filter(Boolean) as Array<turf.Feature<turf.Polygon>>;
};

const createMarker = (
  map: mapboxgl.Map,
  point: mapboxgl.LngLatLike,
  icon: string,
  markerIndex: number,
  id: number,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  isSelected: boolean
): mapboxgl.Marker => {
  const markerId = `marker${markerIndex + 1}-${id}`;
  const marker = new mapboxgl.Marker({
    draggable: isSelected,
    element: createCustomMarkerElement(icon),
  })
    .setLngLat(point)
    .addTo(map);

  mapboxMarkers[markerId] = marker;
  return marker;
};

const removeLayerAndSources = (map: mapboxgl.Map, id: number): void => {
  const layerId = `ab-line-layer-${id}`;
  const sourceId = `ab-line-geojson-data-${id}`;
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }

  const marker1Id = `marker1-${id}`;
  const marker2Id = `marker2-${id}`;

  if (mapboxMarkers[marker1Id]) mapboxMarkers[marker1Id].remove();
  if (mapboxMarkers[marker2Id]) mapboxMarkers[marker2Id].remove();

  // Remove regular grid lines
  const temp = map.getSource(`grid-layer-${id}`);
  if (temp) {
    map.removeLayer(`grid-line-layer-${id}`);
    map.removeSource(`grid-layer-${id}`);
  }

  // Remove gray intermediate grid lines
  const graySourceId = `grid-layer-${id}-gray`;
  const grayLayerId = `grid-line-layer-${id}-gray`;
  if (map.getLayer(grayLayerId)) {
    map.removeLayer(grayLayerId);
  }
  if (map.getSource(graySourceId)) {
    map.removeSource(graySourceId);
  }
};

const updateLayerVisibilityAndSelection = (
  map: mapboxgl.Map,
  layerId: string,
  visible: boolean | undefined,
  id: number,
  isSelected: boolean,
  polygonGeoJSON: any
): void => {
  map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");

  const marker1Id = `marker1-${id}`;
  const marker2Id = `marker2-${id}`;
  if (mapboxMarkers[marker1Id]) {
    mapboxMarkers[marker1Id].getElement().style.display = visible
      ? "block"
      : "none";
    mapboxMarkers[marker1Id].setDraggable(isSelected);
  }
  if (mapboxMarkers[marker2Id]) {
    mapboxMarkers[marker2Id].getElement().style.display = visible
      ? "block"
      : "none";
    mapboxMarkers[marker2Id].setDraggable(isSelected);
  }

  const gridId = `grid-line-layer-${id}`;
  if (map.getLayer(gridId)) {
    map.setLayoutProperty(gridId, "visibility", visible ? "visible" : "none");
  }

  const grayGridId = `grid-line-layer-${id}-gray`;
  if (map.getLayer(grayGridId)) {
    map.setLayoutProperty(
      grayGridId,
      "visibility",
      visible ? "visible" : "none"
    );
  }
};

const updateABWidth = (
  abItem: AbLine,
  sourceId: string,
  map: mapboxgl.Map,
  polygonGeoJSON: PolygonGeoJSON
): void => {
  if (abItem.grid_width === 0) return;

  const bbox = turf.bbox(polygonGeoJSON.features[FEATURES_INDEX_ZERO].geometry);
  const expandedBbox = expandBbox(bbox);
  const grid = turf.squareGrid(
    expandedBbox as turf.helpers.BBox,
    abItem.grid_width as number,
    { units: "meters" }
  );
  const gridRotation = calculateAngle(abItem);
  const rotatedGrid = turf.transformRotate(grid, gridRotation);
  const gridWithinPolygon = getGridWithinPolygon(rotatedGrid, polygonGeoJSON);

  (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData({
    type: "FeatureCollection",
    features: gridWithinPolygon,
  });
};

const calculateAngle = (abItem: AbLine): number => {
  const coordinates = abItem.geojson.geometry.coordinates;
  const bearing1 = turf.bearing(turf.point([0, 0]), turf.point([0, 0])); // Assume fixed line
  const bearing2 = turf.bearing(
    turf.point(coordinates[0]),
    turf.point(coordinates[1])
  ); // Actual line
  let angle = Math.abs(bearing2 - bearing1);
  return bearing2 < 0 ? -angle : angle;
};

// Exports for testing internal functions
export { getLinePoints, getMachineryGridArray, calculateAngleDegrees };
